import React from "react";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LayoutGrid, Trophy, Heart, Map, Sparkles } from "lucide-react";
import { useAuth } from "../auth/AuthContext";

/**
 * MobileTabBar — fixed bottom navigation visible only on portrait-mobile
 * widths (`md:hidden`, i.e. < 768px).
 *
 * Web-Parity Cycle (2026-06-18): replaced "Einladen" with "Heatmap"
 * so the 5-tab bar exposes the Heatmap surface that landed in
 * Cycle B.5. Referral stays reachable via `/profile` → "Einladen /
 * Share & Earn" (already wired in ProfilePage).
 *
 *   Deals · Top Deals · Heatmap · Favoriten · Profil     (exactly 5)
 *
 * Admin users mirror native iOS: no separate Admin tab — the admin
 * console link lives INSIDE the Profile screen so the bottom bar stays
 * 5 tabs across both roles.
 *
 * Icons (lucide-react):
 *   Deals      → LayoutGrid
 *   Top Deals  → Trophy        (emerald active tone)
 *   Heatmap    → Map           (new)
 *   Favoriten  → Heart
 *   Profil     → UserIcon
 */
const TABS = [
  {
    to: "/",
    end: true,
    labelKey: "nav.discover",
    icon: LayoutGrid,
    testId: "mobile-tab-discover",
  },
  {
    to: "/heatmap",
    labelKey: "nav.heatmap",
    icon: Map,
    testId: "mobile-tab-heatmap",
  },
  {
    to: "/ai",
    labelKey: "nav.ai",
    icon: Sparkles,
    testId: "mobile-tab-ai",
    activeTone: "amber",
    hero: true,
  },
  {
    to: "/alerts",
    labelKey: "nav.alerts",
    icon: Trophy,
    testId: "mobile-tab-alerts",
  },
  {
    to: "/favorites",
    labelKey: "nav.favorites",
    icon: Heart,
    testId: "mobile-tab-favorites",
  },
];

function TabItem({ to, labelKey, icon: Icon, testId, activeTone, hero, end = false }) {
  const { t } = useTranslation();
  const label = t(labelKey);
  // Top Deals gets the emerald accent to match the native app's
  // "premium feature" signal (native uses tabBarActiveTintColor =
  // colors.emerald400 on that tab specifically).
  const activeClass =
    activeTone === "emerald" ? "text-emerald-400" : "text-amber-500";

  // V4.7 P1 — the AI tab is the hero: elevated orb with a subtle amber
  // hairline ring. Premium raise, no confetti.
  if (hero) {
    return (
      <NavLink
        to={to}
        data-testid={testId}
        className={({ isActive }) =>
          [
            "flex h-full flex-1 flex-col items-center justify-center gap-0.5 text-[10px] font-medium",
            "transition-colors",
            isActive ? "text-amber-400" : "text-zinc-400 hover:text-zinc-200",
          ].join(" ")
        }
      >
        {({ isActive }) => (
          <>
            <span
              className={[
                "flex h-11 w-11 -translate-y-3.5 items-center justify-center rounded-full border bg-zinc-900",
                "shadow-lg transition-all",
                isActive
                  ? "border-amber-400/70 shadow-amber-500/20"
                  : "border-amber-500/35 shadow-amber-500/10",
              ].join(" ")}
              aria-hidden="true"
            >
              <Icon
                size={20}
                strokeWidth={isActive ? 2.2 : 1.8}
                className={isActive ? "text-amber-400" : "text-amber-500/80"}
              />
            </span>
            <span className="-mt-2.5 leading-none">{label}</span>
          </>
        )}
      </NavLink>
    );
  }

  return (
    <NavLink
      to={to}
      data-testid={testId}
      end={end}
      className={({ isActive }) =>
        [
          "flex h-full flex-1 flex-col items-center justify-center gap-1 text-[10px] font-medium",
          "transition-colors",
          isActive ? activeClass : "text-zinc-500 hover:text-zinc-200",
        ].join(" ")
      }
    >
      {({ isActive }) => (
        <>
          <Icon
            size={20}
            strokeWidth={isActive ? 2.4 : 1.8}
            className="shrink-0"
            aria-hidden="true"
          />
          <span className="leading-none">{label}</span>
        </>
      )}
    </NavLink>
  );
}

export default function MobileTabBar() {
  const { user } = useAuth();

  // Authed-only — bottom bar is meaningless for logged-out users.
  if (!user) return null;

  return (
    <nav
      data-testid="mobile-tab-bar"
      aria-label="App navigation"
      className="fixed inset-x-0 z-40 flex h-[64px] items-stretch border-t border-zinc-800 bg-zinc-950/95 backdrop-blur pb-[env(safe-area-inset-bottom,0)] md:hidden"
      style={{ bottom: "var(--consent-h, 0px)", paddingBottom: "max(env(safe-area-inset-bottom,0), 0px)" }}
    >
      {TABS.map((t) => (
        <TabItem key={t.to} {...t} />
      ))}
    </nav>
  );
}
